import type { ReactNode } from "react";
import { Card, CardContent, Skeleton } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type { BrokeragePipelineMix, BrokerageProductionSubBroker } from "../api/brokerage";

// Brokerage / Sub-Broker PR-6 — the small, shared presentational pieces for the
// brokerage portal data tabs. Portal-local (design-system kit only), so the
// isolation boundary stays intact.

export function StatTile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-space-1">
        <p className="text-caption uppercase tracking-wide text-gray-mid">{label}</p>
        <div className="text-h4 font-semibold text-onyx">{children}</div>
      </CardContent>
    </Card>
  );
}

export function LoadingTiles() {
  return (
    <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="brokerage-loading">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

const STAGE_LABELS: Record<string, string> = {
  Intake: "Intake",
  PreApproval: "Pre-approval",
  Underwriting: "Underwriting",
  Closing: "Closing",
  Closed: "Closed",
  Declined: "Declined",
  Withdrawn: "Withdrawn",
};

export function PipelineMixBars({ mix }: { mix: BrokeragePipelineMix[] }) {
  const shown = mix.filter((m) => STAGE_LABELS[m.stage]);
  const max = Math.max(1, ...shown.map((m) => m.count));
  if (shown.every((m) => m.count === 0)) {
    return <p className="text-small text-gray-mid">No deals in the pipeline yet.</p>;
  }
  return (
    <div className="space-y-space-2">
      {shown.map((m) => (
        <div key={m.stage} className="flex items-center gap-space-3">
          <span className="w-28 shrink-0 text-caption text-gray-mid">{STAGE_LABELS[m.stage]}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-white-warm">
            <div
              className="h-full rounded-full bg-gold"
              style={{ width: `${(m.count / max) * 100}%` }}
            />
          </div>
          <span className="w-8 shrink-0 text-right text-caption font-semibold text-onyx">
            {m.count}
          </span>
        </div>
      ))}
    </div>
  );
}

export function SubBrokerTable({ rows }: { rows: BrokerageProductionSubBroker[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border-light">
      <table className="w-full text-small">
        <thead>
          <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
            <th className="px-space-3 py-space-2 font-medium">Sub-broker</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Deals</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Gross</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.subBrokerId} className="border-b border-border-light last:border-0">
              <td className="px-space-3 py-space-2 text-onyx">{r.subBrokerName}</td>
              <td className="px-space-3 py-space-2 text-right text-gray-mid">{r.dealCount}</td>
              <td className="px-space-3 py-space-2 text-right text-onyx">
                {formatCurrency(r.ledgerGross)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
